"use client";
import React, { useState, useEffect, useRef } from "react";
import { IoArrowBackSharp, IoArrowForwardSharp } from "react-icons/io5";
import apiService from "../services/apiService";
import "./ImageCarousel.css";

const ImageCarousel = ({ images = [], onReorder, propertyId }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const trackRef = useRef(null);
  const isMovingRef = useRef(false);

  // Drag and drop state
  const dndActiveRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedOriginalIndex, setDraggedOriginalIndex] = useState(null);
  const [imageObjects, setImageObjects] = useState([]);
  const [dropIndex, setDropIndex] = useState(null);

  // Touch/swipe state
  const swipeRef = useRef({
    startX: 0,
    startY: 0,
    isDragging: false,
    hasMoved: false,
    startTime: 0,
  });

  // Keep objects for ordering and displayOrder - ensure proper sorting
  useEffect(() => {
    const filtered = images
      .filter((img) => img.isActive && img.mediaLink && img.title === "Gallery")
      .sort((a, b) => a.displayOrder - b.displayOrder); // This should sort from lowest to highest displayOrder
    setImageObjects(filtered);
  }, [images]);

  const originalImages = imageObjects.map((img) => img.mediaLink);

  // Create extended array for smooth infinite scroll (desktop only)
  const extendedImages = [
    ...originalImages,
    ...originalImages,
    ...originalImages,
  ];

  // Update currentIndex when originalImages changes
  useEffect(() => {
    if (originalImages.length > 0) {
      setCurrentIndex(originalImages.length); // Start in the middle set
    }
  }, [originalImages.length]);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") goToPrev();
      if (e.key === "ArrowRight") goToNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Fallback to empty array if no valid images
  if (originalImages.length === 0) {
    return (
      <div className="carousel-no-images">
        <div className="carousel-no-images-text">No images available</div>
      </div>
    );
  }

  // Navigation functions with smooth infinite scroll (desktop)
  const goToNext = () => {
    if (isMovingRef.current) return;
    isMovingRef.current = true;

    if (isMobile) {
      // Mobile: simple index increment with wrap
      setSelectedImageIndex((prev) =>
        prev === originalImages.length - 1 ? 0 : prev + 1
      );
      isMovingRef.current = false;
      return;
    }

    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);

    // Reset position when reaching the end of the middle set
    setTimeout(() => {
      if (currentIndex >= originalImages.length * 2 - 1) {
        setIsTransitioning(false);
        setCurrentIndex(originalImages.length);
        requestAnimationFrame(() => {
          setIsTransitioning(true);
        });
      }
      isMovingRef.current = false;
    }, 300);
  };

  const goToPrev = () => {
    if (isMovingRef.current) return;
    isMovingRef.current = true;

    if (isMobile) {
      // Mobile: simple index decrement with wrap
      setSelectedImageIndex((prev) =>
        prev === 0 ? originalImages.length - 1 : prev - 1
      );
      isMovingRef.current = false;
      return;
    }

    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);

    // Reset position when reaching the beginning of the middle set
    setTimeout(() => {
      if (currentIndex <= originalImages.length) {
        setIsTransitioning(false);
        setCurrentIndex(originalImages.length * 2 - 1);
        requestAnimationFrame(() => {
          setIsTransitioning(true);
        });
      }
      isMovingRef.current = false;
    }, 300);
  };

  // Utility functions for desktop
  const getImageWidth = () => {
    // Responsive image width based on screen size
    if (window.innerWidth >= 1536) return 400; // 2xl
    if (window.innerWidth >= 1280) return 350; // xl
    return 300; // lg
  };

  const getTransform = () => {
    const imageWidth = getImageWidth();
    const gap = 16; // 1rem gap
    return `translateX(calc(-${currentIndex * (imageWidth + gap)}px + 50% - ${
      imageWidth / 2
    }px))`;
  };

  const shouldLoadImage = (index) => {
    const distance = Math.abs(index - currentIndex);
    return distance <= 6;
  };

  // Enhanced touch/swipe handlers
  const handlePointerDown = (e) => {
    if (isMovingRef.current) return;

    const swipe = swipeRef.current;
    swipe.startX = e.clientX;
    swipe.startY = e.clientY;
    swipe.isDragging = true;
    swipe.hasMoved = false;
    swipe.startTime = Date.now();

    const handlePointerMove = (moveEvent) => {
      if (!swipe.isDragging) return;

      const deltaX = Math.abs(moveEvent.clientX - swipe.startX);
      const deltaY = Math.abs(moveEvent.clientY - swipe.startY);

      // Only consider it a swipe if horizontal movement is greater than vertical
      if (deltaX > 10 || deltaY > 10) {
        if (deltaX > deltaY) {
          swipe.hasMoved = true;
          // Prevent click events on images during swipe
          e.preventDefault();
        }
      }

      // Trigger swipe if moved enough horizontally
      if (deltaX > 50 && deltaX > deltaY && !swipe.hasMoved) {
        swipe.hasMoved = true;
        const direction = moveEvent.clientX - swipe.startX;
        if (direction > 0) goToPrev();
        else goToNext();
        cleanup();
      }
    };

    const handlePointerUp = () => {
      swipe.isDragging = false;
      cleanup();
    };

    const cleanup = () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };

    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);
  };

  // Handle image button clicks (prevent if swiping)
  const handleImageButtonClick = (e, index) => {
    const swipe = swipeRef.current;
    const clickDuration = Date.now() - swipe.startTime;

    // If it was a quick tap without movement, treat as click
    if (!swipe.hasMoved && clickDuration < 300) {
      // Click handled
    } else {
      e.preventDefault();
    }
  };

  // Mobile single image view
  if (isMobile) {
    return (
      <div className="carousel-container">
        <div
          className="carousel-mobile-container"
          onPointerDown={handlePointerDown}
        >
          <button
            className="carousel-mobile-button"
            onClick={(e) => handleImageButtonClick(e, selectedImageIndex)}
          >
            <img
              src={originalImages[selectedImageIndex]}
              alt={`Image ${selectedImageIndex + 1}`}
              className="carousel-image"
              loading="eager"
              draggable={false}
            />
            <div className="carousel-hover-overlay" />
          </button>
        </div>

        {/* Mobile indicator dots */}
        {originalImages.length > 1 && (
          <div className="carousel-mobile-dots">
            {originalImages.map((_, index) => (
              <div
                key={index}
                className={`carousel-mobile-dot ${
                  index === selectedImageIndex
                    ? "carousel-mobile-dot-active"
                    : "carousel-mobile-dot-inactive"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Desktop carousel view
  return (
    <div className="carousel-container">
      {/* Navigation Arrows */}
      <button
        onClick={goToPrev}
        className="carousel-arrow carousel-arrow-left"
        aria-label="Previous image"
      >
        <IoArrowBackSharp size={50} color="white" />
      </button>

      <button
        onClick={goToNext}
        className="carousel-arrow carousel-arrow-right"
        aria-label="Next image"
      >
        <IoArrowForwardSharp size={50} color="white" />
      </button>

      {/* Carousel Container */}
      <div
        className="carousel-desktop-container"
        onPointerDown={handlePointerDown}
      >
        {/* Image Track */}
        <div
          ref={trackRef}
          className={`carousel-track ${
            isTransitioning ? "carousel-track-transition" : ""
          }`}
          style={{ transform: getTransform() }}
        >
          {extendedImages.map((image, index) => {
            const imageWidth = getImageWidth();
            return (
              <button
                key={`${image}-${index}`}
                className="carousel-image-button"
                style={{ width: `${imageWidth}px` }}
                onClick={(e) => handleImageButtonClick(e, index)}
              >
                {shouldLoadImage(index) ? (
                  <>
                    <img
                      src={image}
                      alt={`Image ${(index % originalImages.length) + 1}`}
                      className="carousel-image"
                      loading="eager"
                      draggable={false}
                    />
                    <div className="carousel-hover-overlay" />
                  </>
                ) : (
                  <div className="carousel-loading">
                    <div className="carousel-loading-text">Loading...</div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ImageCarousel;
